import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Skeleton } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals/ModalCDS/ModalCDS.constants';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_REQUEST_DATETIME_FORMAT } from 'constants/date';

import { openDialogAction } from 'store/global/actions';

import PreInvestorBulkPasteModal from './PreInvestorBulkPasteModal';
import PreInvestorConditionActions from './PreInvestorConditionActions';
import PreInvestorConditionDrawer from './PreInvestorConditionDrawer';
import { getPreConditionsTableConfig } from './PreInvestorConditions.config';
import { addButtonWrapperStyles } from './PreInvestorConditions.styles';
import { PreInvestorConditionsTestIds } from './PreInvestorConditions.testIds';

const PreInvestorConditionsTable = ({
  conditions = [],
  isLoading,
  isEditAllowed,
  onSaveCondition,
  onDeleteCondition,
  onMarkResponded,
  onMarkCleared,
  onBulkAddConditions,
}) => {
  const dispatch = useDispatch();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [drawerKey, setDrawerKey] = useState(0);

  const openConditionDrawer = useCallback((condition = null) => {
    const record = condition?.key ? condition : null;
    setEditingCondition(record);
    setDrawerKey(prev => prev + 1);
    setIsDrawerOpen(true);
  }, []);

  const closeConditionDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingCondition(null);
  }, []);

  const onOpenBulkPaste = useCallback(() => {
    dispatch(
      openDialogAction({
        isRestyled: true,
        header: 'Bulk Paste Conditions',
        size: ModalSize.LARGE,
        body: dialogProps => (
          <PreInvestorBulkPasteModal
            dialogId={dialogProps.dialogId}
            onAddConditions={onBulkAddConditions}
          />
        ),
      }),
    );
  }, [dispatch, onBulkAddConditions]);

  const onMarkRespondedForRow = useCallback(
    conditionId => {
      const today = dayjs().format(COMMON_REQUEST_DATETIME_FORMAT);
      const updated = conditions.map(condition =>
        condition.id === conditionId ? { ...condition, dateResponded: today } : condition,
      );
      onMarkResponded(updated);
    },
    [conditions, onMarkResponded],
  );

  const onMarkClearedForRow = useCallback(
    conditionId => {
      const today = dayjs().format(COMMON_REQUEST_DATETIME_FORMAT);
      const updated = conditions.map(condition =>
        condition.id === conditionId ? { ...condition, dateCleared: today } : condition,
      );
      onMarkCleared(updated);
    },
    [conditions, onMarkCleared],
  );

  const renderConditionActions = useCallback(
    row => (
      <PreInvestorConditionActions
        isEditAllowed={isEditAllowed}
        row={row}
        onDelete={onDeleteCondition}
        onEdit={openConditionDrawer}
        onMarkCleared={onMarkClearedForRow}
        onMarkResponded={onMarkRespondedForRow}
      />
    ),
    [
      isEditAllowed,
      openConditionDrawer,
      onDeleteCondition,
      onMarkRespondedForRow,
      onMarkClearedForRow,
    ],
  );

  const tableConfig = useMemo(
    () => getPreConditionsTableConfig({ isEditAllowed, renderActions: renderConditionActions }),
    [isEditAllowed, renderConditionActions],
  );

  const data = useMemo(
    () =>
      conditions.map((condition, index) => ({
        ...condition,
        key: condition.id ?? `condition-${index}`,
      })),
    [conditions],
  );

  if (isLoading) {
    return <Skeleton height={100} variant="rectangular" />;
  }

  return (
    <>
      {isEditAllowed && (
        <Box sx={addButtonWrapperStyles}>
          <Button
            data-testid={PreInvestorConditionsTestIds.ADD_CONDITION_BUTTON}
            size="small"
            startIcon={<AddBoxIcon size={IconSize[16]} />}
            onClick={openConditionDrawer}
          >
            Add Condition
          </Button>
          <Button
            data-testid={PreInvestorConditionsTestIds.BULK_PASTE_BUTTON}
            size="small"
            variant="line"
            onClick={onOpenBulkPaste}
          >
            Bulk Paste
          </Button>
        </Box>
      )}
      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={data}
        isLoading={isLoading}
        skeletonRows={3}
      />
      <PreInvestorConditionDrawer
        condition={editingCondition}
        key={drawerKey}
        open={isDrawerOpen}
        onClose={closeConditionDrawer}
        onSave={onSaveCondition}
      />
    </>
  );
};

PreInvestorConditionsTable.propTypes = {
  conditions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      conditionText: PropTypes.string,
      dateAdded: PropTypes.string,
      dateResponded: PropTypes.string,
      dateCleared: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  isEditAllowed: PropTypes.bool,
  onSaveCondition: PropTypes.func,
  onDeleteCondition: PropTypes.func,
  onMarkResponded: PropTypes.func,
  onMarkCleared: PropTypes.func,
  onBulkAddConditions: PropTypes.func,
};

export default memo(PreInvestorConditionsTable);
